import { Inject, Injectable, Optional, inject } from "@angular/core";
import {
  Resolve,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from "@angular/router";
import { Observable, firstValueFrom, map } from "rxjs";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Meta, Title } from "@angular/platform-browser";
import { REQUEST } from "@nguniversal/express-engine/tokens";
import { Request } from "express";
import { SeoService } from "src/app/services/seo.service";
import { buildSEOImageUrl } from "src/app/utils/seo-image.helper";

interface CollectionItem {
  id: string;
  collectionName?: string;
  thumbUrl?: string;
  isExclusive?: boolean;
  isFeatured?: boolean;
  isLatest?: boolean;
  isPopular?: boolean;
  isAICollection?: boolean;
}

@Injectable({ providedIn: "root" })
export class CollectionsResolver implements Resolve<any> {
  afs = inject(AngularFirestore);
  constructor(
    private meta: Meta, // private socialService: SocialService,
    private title: Title,
    public router: Router,
    private seo: SeoService
  ) { }
  async resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<any> {
    const slug = route.paramMap.get("slug");
    const ref = this.afs.collection("filesMaster", (ref) =>
      ref.where("slug", "==", slug).orderBy("createdOn", "desc")
    );
    const data$ = ref.valueChanges().pipe(
      map((res) => {
        // this.populateMetaTags(res[0], route);
        this.populateMetaTags();
        return res[0];
      })
    ); // Get single item

    return await firstValueFrom(data$);
  }

  async populateMetaTags() {
    //console.log(route.paramMap);
    const title = "Collections | Illusbook";
    const desc =
      "Explore beautiful illustration collections on Illusbook for business, technology, education, people and more. Find high-quality visuals for apps, websites, presentations, and marketing projects.";
    // const image = "assets/img/illusbook.png";
    //console.log("Router URL:", this.router);
    // const protocol =
    //   this.request.headers["x-forwarded-proto"] || this.request.protocol;
    // const host = this.request.headers.host;
    // const originalUrl = this.request.originalUrl;
    // const fullURL = `${protocol}://${host}${originalUrl}`;
    const fullURL = `https://illusbook.com/collection`;
    //console.log("Window URL:", fullURL);
    this.title.setTitle(`${title}`);
    this.setMetaTag("name", "title", title);
    this.setMetaTag("name", "description", desc);
    this.setMetaTag("name", "robots", "index, follow, max-image-preview:large");
    this.setMetaTag("property", "og:title", title);
    this.setMetaTag("property", "og:description", desc);
    this.setMetaTag("property", "og:url", fullURL);
    this.setMetaTag("property", "og:type", "website");
    this.setMetaTag("property", "og:site_name", "Illusbook");
    this.setMetaTag("name", "twitter:card", "summary_large_image");
    this.setMetaTag("name", "twitter:title", title);
    this.setMetaTag("name", "twitter:description", desc);

    const ORGANIZATION_SCHEMA = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://illusbook.com/#organization",
      "name": "Illusbook",
      "url": "https://illusbook.com",
      "logo": "https://illusbook.com/assets/favicon/android-icon-192x192.png",
      "description": "Illusbook offers a collection of stunning illustrations. Browse and download the perfect illustrations for your projects today.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "email": "support@illusbook.com",
        "availableLanguage": ["English"]
      },
      "sameAs": [
        "https://www.linkedin.com/company/illusbook/",
        "https://www.instagram.com/illusbook/",
        "https://x.com/illusbook",
        "https://www.pinterest.com/illusbook/"
      ]
    };



    // ---- FETCH ALL COLLECTIONS (same as component) ----
    const collections = await firstValueFrom(
      this.afs.collection<CollectionItem>("collectionMaster",
        ref => ref.orderBy("createdOn", "desc")
      ).valueChanges({ idField: "id" })
    );

    // ---- Apply SAME LOGIC as your UI ----
    const selected: CollectionItem[] = [];

    for (const item of collections) {

      if (item.isAICollection) continue;

      if (item.isExclusive && selected.length < 6) selected.push(item);
      else if (item.isFeatured && selected.length < 6) selected.push(item);
      else if (item.isLatest && selected.length < 6) selected.push(item);
      else if (item.isPopular && selected.length < 6) selected.push(item);
    }

    // ---- Build Rich Images ----
    const galleryImages = selected
      .filter(item => item.thumbUrl)
      .map(item => {
        const img = buildSEOImageUrl(item.thumbUrl);

        return {
        "@type": "ImageObject",
        contentUrl: img,
        thumbnailUrl: img,
        name: item.collectionName || "Illustration Collection",
        "license": "https://illusbook.com/license",
        "acquireLicensePage": "https://illusbook.com/pricing",
        "creditText": "Designed by Illusbook",

        creator: {
          "@type": "Organization",
          name: "Illusbook",
        },

        "copyrightNotice": "Illusbook",
        }


      });

                // Use real image for social preview
    const heroImage = galleryImages[0]?.contentUrl || "https://illusbook.com/assets/img/illusbook.png";

    this.setMetaTag("property", "og:image", heroImage);
    this.setMetaTag("property", "og:image:width", "1200");
    this.setMetaTag("property", "og:image:height", "1200");
    this.setMetaTag("property", "og:image:type", "image/jpeg");
    this.setMetaTag("name", "twitter:image", heroImage);


      const COLLECTION_SCHEMA: any = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: desc,
      url: fullURL,
      primaryImageOfPage: galleryImages[0] || {
      "@type": "ImageObject",
      contentUrl: heroImage,
      thumbnailUrl: heroImage,
      name: title,
    },
     mainEntity: {
        "@type": "ImageGallery",
        associatedMedia: galleryImages
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://illusbook.com" },
          { "@type": "ListItem", position: 2, name: "Collections", item: fullURL }
        ]
      },
      publisher: { "@id": "https://illusbook.com/#organization" },
      isPartOf: { "@id": "https://illusbook.com/#website" },
      inLanguage: "en"
    }


    // ---- JSON-LD FOR GOOGLE ----
    this.seo.setJSONLD([COLLECTION_SCHEMA,
      ORGANIZATION_SCHEMA
    ]);


  }

  setMetaTag(
    attrName: "name" | "property",
    attrValue: string,
    content: string
  ) {
    const selector = `${attrName}="${attrValue}"`;
    //console.log(selector);
    this.meta.removeTag(selector);
    this.meta.addTag({ [attrName]: attrValue, content });
    // this.meta.updateTag({ [attrName]: attrValue, content });
  }
}
